/**
 * How an upgrade application is FILED for the secretariat's list — which is not
 * always what its status column says.
 *
 * A Membership Committee member recommending approval does not finalise
 * anything; only the chairman's ruling does. So a recommended application would
 * otherwise sit in Under Review looking untouched. These predicates decide
 * which panel a row belongs to, and they live here rather than in the page
 * because the exports have to file every row exactly where the on-screen tabs
 * put it — two copies of this reasoning would eventually disagree, and the
 * report would then contradict the screen it was printed from.
 */

export type StatusKey =
  | "all"
  | "draft"
  | "pending_payment"
  | "payment_unsettled"
  | "submitted"
  | "awaiting_endorsements"
  | "under_review"
  | "fully_endorsed"
  | "returned"
  | "recommended"
  | "not_recommended"
  | "approved"
  | "rejected";

export const STATUS_TABS: { key: StatusKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "pending_payment", label: "Awaiting Payment" },
  { key: "payment_unsettled", label: "Unpaid (has payment)" },
  { key: "submitted", label: "Submitted" },
  { key: "awaiting_endorsements", label: "Awaiting Endorsements" },
  { key: "fully_endorsed", label: "Fully Endorsed" },
  { key: "under_review", label: "Under Review" },
  { key: "returned", label: "Returned" },
  { key: "recommended", label: "Recommended" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

export const statusOf = (r: any) => String(r?.status ?? "").toLowerCase();

/**
 * A payment was started or recorded but never settled — an abandoned online
 * attempt, most often. Cuts across the status column rather than living in it.
 */
export const isPaymentUnsettled = (r: any) => {
  const s = String(r?.payment?.status ?? "").toLowerCase();
  return !!r?.payment && s !== "paid";
};

/**
 * At least one Membership Committee member has recommended approval. The status
 * is the authority — a recommendation now moves the application to
 * 'recommended' — but the review tally still counts, so a row stays put even
 * where an older record never had its status moved.
 */
export const isRecommended = (r: any) =>
  statusOf(r) === "recommended" || Number(r?.committee_approved_count ?? 0) > 0;

/** The chairman has already ruled — that decision outranks a recommendation. */
export const isFinalised = (r: any) => ["approved", "rejected"].includes(statusOf(r));

/**
 * Recommended and still in flight. It has its own panel: a recommendation is
 * the committee's opinion, not the Institute's decision, and showing the two
 * in one list made an application look approved before the chairman had ruled.
 */
export const isRecommendedPending = (r: any) => isRecommended(r) && !isFinalised(r);

/**
 * The committee decided AGAINST it. Not finalised — the chairman still rules —
 * but it belongs with the rejections rather than in the review queue.
 */
export const isNotRecommended = (r: any) => statusOf(r) === "not_recommended";

/** Approved means approved: the chairman signed it off. */
export const inApprovedSection = (r: any) => statusOf(r) === "approved";

/** The committee's recommendations, awaiting the chairman. */
export const inRecommendedSection = (r: any) => isRecommendedPending(r);

/** Genuinely rejected, plus the committee's recommendations against. */
export const inRejectedSection = (r: any) =>
  statusOf(r) === "rejected" || isNotRecommended(r);

/**
 * Every endorsement is in: the chapter has accepted, and BOTH referees have
 * approved. That is the point at which an application is complete enough to go
 * to the committee, so it is the set the secretariat exports and works from.
 *
 * Cross-cutting, like an unsettled payment — an application does not stop being
 * "submitted" or "under review" by becoming fully endorsed — so it is a filter
 * over the other panels rather than a place a row lives.
 *
 * Both referee slots are required, not merely "every referee named": an
 * application with one referee who has approved is not fully endorsed, it is
 * short a referee.
 */
export const isFullyEndorsed = (r: any) =>
  chapterState(r) === "approved" &&
  refereeCell(r, 0).state === "approved" &&
  refereeCell(r, 1).state === "approved";

/**
 * Which panel this row is filed under — one answer, used both to count and to
 * filter, so a tab's pill can never promise rows the tab does not show.
 *
 * Never returns a cross-cutting key: those are matched by predicate in
 * matchesStatusTab() and counted separately.
 */
export function panelOf(r: any): StatusKey {
  if (isRecommendedPending(r)) return "recommended";
  if (isNotRecommended(r)) return "rejected";
  return statusOf(r) as StatusKey;
}

/** Does this row belong in the given tab? */
export function matchesStatusTab(r: any, tab: StatusKey): boolean {
  if (tab === "all") return true;
  if (tab === "payment_unsettled") {
    // A committee verdict is filed with its verdict, not with its billing.
    return isPaymentUnsettled(r) && !isRecommendedPending(r) && !isNotRecommended(r);
  }
  // Cross-cutting: shown whatever else the application is, because the
  // endorsements are complete regardless of where it has got to since.
  if (tab === "fully_endorsed") return isFullyEndorsed(r);

  return panelOf(r) === tab;
}

/** Free-text search over the fields the list shows. */
export function matchesQuery(r: any, query: string): boolean {
  const nq = query.trim().toLowerCase();
  if (!nq) return true;

  return [
    r.id, r.user?.name, r.user?.email, r.user?.member?.membership_no,
    r.from_category?.name, r.route, r.reference,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(nq);
}

/** Head-count per panel, including the cross-cutting unsettled-payment count. */
export function countsByTab(rows: any[]): Map<StatusKey, number> {
  const counts = new Map<StatusKey, number>();
  counts.set("all", rows.length);

  for (const r of rows) {
    const key = panelOf(r);
    counts.set(key, (counts.get(key) || 0) + 1);
  }

  // Cross-cutting keys are counted separately: panelOf() never returns them,
  // so the loop above cannot have reached them.
  counts.set(
    "payment_unsettled",
    rows.filter((r) => matchesStatusTab(r, "payment_unsettled")).length,
  );
  counts.set("fully_endorsed", rows.filter(isFullyEndorsed).length);

  return counts;
}

/* ── Endorsements ────────────────────────────────────────────────────────── */

export type EndorseState = "approved" | "rejected" | "pending" | "missing";

export const ENDORSE_LABELS: Record<EndorseState, string> = {
  approved: "Accepted",
  rejected: "Declined",
  pending: "Awaiting",
  missing: "Not named",
};

/**
 * The chapter has no "declined" field — only `chapter_accepted_at` — so a
 * chapter is either endorsed or still owing a response.
 */
export function chapterState(r: any): EndorseState {
  if (!r?.chapter_id && !r?.chapter_other) return "missing";
  return r?.chapter_accepted_at ? "approved" : "pending";
}

/** The nth referee (0-based) with their decision, in a stable order. */
export function refereeCell(r: any, index: number) {
  const ref = (r?.referees ?? [])[index];
  if (!ref) return { name: null, state: "missing" as EndorseState, on: null };

  const m = ref.member;
  const name = m
    ? [m.surname, m.firstname].filter(Boolean).join(" ") || m.membership_no
    : ref.member_id ?? null;

  const decision = String(ref.decision ?? "").toLowerCase();
  const state: EndorseState =
    decision === "approved" ? "approved" : decision === "rejected" ? "rejected" : "pending";

  return { name, state, on: ref.approved_at ?? ref.rejected_at ?? null };
}

/* ── Formatting ──────────────────────────────────────────────────────────── */

export const naira = (n?: number | null) =>
  n == null ? "—" : `₦${Number(n).toLocaleString("en-NG")}`;

export function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** The label a tab shows, for naming an export after what it contains. */
export function tabLabel(key: StatusKey): string {
  return STATUS_TABS.find((t) => t.key === key)?.label ?? String(key);
}
